import { gql, useMutation } from '@apollo/client';
import Container from '@material-ui/core/Container';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import VisibilityIcon from '@material-ui/icons/Visibility';
import IconButton from '@material-ui/core/IconButton';
import { useTranslation } from 'react-i18next';

import { GenericResponse } from 'types/schema.type';
import { getTime } from '../utils/getTime';
import { SequenceEpisodes } from '../utils/getSequenceEpisodes';
import { formatSeconds } from '../utils/formatSeconds';

export const EDIT_INTERVAL_ANNOTATION = gql`
  mutation EditInterval($input: EditIntervalInput!) {
    editInterval(input: $input) {
      error
      success
    }
  }
`;

const useStyles = makeStyles((theme) => ({
  root: {
    width: 800,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  table: {
    width: 550,
  },
  cell: {
    border: 'none',
  },
  cellSummary: {
    borderBottom: 'none',
    borderTop: '1px solid rgba(224, 224, 224, 1)',
  },
  title: {
    paddingBottom: theme.spacing(1),
  },
}));

type AFibTableProps = {
  afibs: SequenceEpisodes['afibs'];
  edit?: boolean;
};

export function AFibTable(props: AFibTableProps) {
  const classes = useStyles();
  const { t } = useTranslation();

  const { afibs, edit } = props;

  const [editInterval] = useMutation<{
    togglePauseEpisodeValidation: GenericResponse;
  }>(EDIT_INTERVAL_ANNOTATION);

  const getDuration = (from: number, to: number) => (to - from) / 200;

  const getTotalDuration = () => {
    const time = afibs?.reduce((acc, ep) => {
      if (ep.isValid) {
        return acc + getDuration(ep.startIndex, ep.endIndex);
      }

      return acc;
    }, 0);

    const formattedTime = formatSeconds(time);

    return formattedTime;
  };

  const renderEmpty = () => (
    <TableBody>
      <TableRow>
        <TableCell className={classes.cell}>{t('afibTable.noneDetected')}</TableCell>
      </TableRow>
    </TableBody>
  );

  const handleEditInterval = async (episode) => {
    if (!episode) return;

    await editInterval({
      variables: {
        input: {
          annotationId: episode.id,
          startIndex: episode.startIndex,
          endIndex: episode.endIndex,
          isValid: !episode.isValid,
        },
      },
      update(cache) {
        cache.modify({
          id: cache.identify(episode),
          fields: {
            isValid(existingIsValid = false) {
              return !existingIsValid;
            },
          },
        });
      },
    });
  };

  const episodesToShow = edit ? afibs : afibs?.filter((ep) => ep.isValid);

  const renderFull = () => (
    <>
      <TableHead>
        <TableRow>
          <TableCell>{t('afibTable.columns.from')}</TableCell>
          <TableCell>{t('afibTable.columns.to')}</TableCell>
          <TableCell>{t('afibTable.columns.duration')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {episodesToShow?.map((ep) => {
          const duration = getDuration(ep.startIndex, ep.endIndex);
          const formattedDuration = formatSeconds(duration);

          return (
            <TableRow key={ep.id}>
              <TableCell className={classes.cell}>
                {getTime(ep.recordingStartedAt ?? 0, ep.startIndex)}
              </TableCell>
              <TableCell className={classes.cell}>
                {getTime(ep.recordingStartedAt ?? 0, ep.endIndex)}
              </TableCell>
              <TableCell className={classes.cell}>{formattedDuration}</TableCell>
              {edit && (
                <IconButton edge="end" aria-label="" onClick={() => handleEditInterval(ep)}>
                  {ep.isValid ? <VisibilityIcon /> : <VisibilityOffIcon />}
                </IconButton>
              )}
            </TableRow>
          );
        })}
        <TableRow>
          <TableCell className={classes.cell} colSpan={1} />
          <TableCell className={classes.cellSummary}>{t('afibTable.columns.total')}</TableCell>
          <TableCell className={classes.cellSummary}>{getTotalDuration()}</TableCell>
        </TableRow>
      </TableBody>
    </>
  );

  return (
    <Container className={classes.root} component={Paper}>
      <Typography className={classes.title} variant="h6">
        {t('afibTable.title')}
      </Typography>
      <Table className={classes.table} size="small">
        {episodesToShow?.length ? renderFull() : renderEmpty()}
      </Table>
    </Container>
  );
}
